import {
    Box,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Textarea,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { RichTextRenderer } from "components/rich-text-renderer";
import { SafeHTML } from "components/safe-html";
import { validateMessage } from "helpers/validation-helper";
import { useBookingActionsContext } from "hooks/use-booking-state";
import { Control, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContactInfoForm } from "store/store-types";
import { resolveRichTextField } from "types/rich-text-field";

interface IMessageFieldProps {
    control: Control<ContactInfoForm, string>;
    value: string;
}

const MessageField = ({ control, value }: IMessageFieldProps) => {
    const { t, i18n } = useTranslation();
    const externalBookingSettings = useExternalBookingSettings().data;
    const { setContact } = useBookingActionsContext();

    const messageDescriptionTemplate =
        externalBookingSettings?.messageTemplates?.messageDescription;

    const headerText = externalBookingSettings?.messageDescription?.length
        ? externalBookingSettings.messageDescription
        : " ";
    const formattedMessageHeaderText = headerText.replace(/\n/g, "<br />");

    const customAddedMessageText = messageDescriptionTemplate ? (
        <RichTextRenderer
            key="messageHeaderTextResult"
            content={resolveRichTextField(messageDescriptionTemplate, i18n.language)}
        />
    ) : (
        headerText.length > 0 && (
            <SafeHTML key="messageHeaderTextResult" html={formattedMessageHeaderText} />
        )
    );

    const {
        fieldState: { error },
        field,
    } = useController({
        name: "message",
        control,
        rules: {
            validate: (value) => validateMessage(value),
        },
    });

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value.length > 255) return;
        field.onChange(e.target.value);
        setContact?.({ message: e.target.value });
    };

    return (
        <FormControl isInvalid={!!error}>
            {customAddedMessageText ? (
                <FormLabel>{customAddedMessageText}</FormLabel>
            ) : (
                <FormLabel>{t("contact.message")}</FormLabel>
            )}
            <Textarea
                id="message"
                name="message"
                value={value}
                onChange={handleMessageChange}
                rows={3}
                isInvalid={!!error}
                onBlur={field.onBlur}
            />
            <Flex justify="space-between">
                <FormErrorMessage>{error?.message}</FormErrorMessage>
                <Box
                    as="span"
                    mt={ThemeSpaceVariable.XSmall}
                    color={
                        value?.length > 254
                            ? ThemeColorVariable.Error
                            : ThemeColorVariable.OnBackgroundSubdued
                    }
                    fontSize={ThemeFontSizeVariable.Small}
                >
                    {`${value?.length ?? 0}/255`}
                </Box>
            </Flex>
        </FormControl>
    );
};

export default MessageField;
